import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Lock, MapPin, Building2, DollarSign, Target, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// 2026 Grant Database
const GRANT_DATABASE = {
  QLD: [
    { id: 'tqmgp', name: 'Tradies Queensland Manufacturing Grant Program', amount: 'Up to $100,000', focus: 'Manufacturing equipment', url: 'https://www.business.qld.gov.au/industries/manufacturing-retail/manufacturing/grants' },
    { id: 'qbgp', name: 'Queensland Business Growth Program', amount: '$25,000–$100,000', focus: 'Business expansion', url: 'https://www.business.qld.gov.au/starting-business/funding-grants' },
    { id: 'sbs', name: 'Small Business Strategy', amount: 'Various', focus: 'SME support', url: 'https://www.business.qld.gov.au/' },
    { id: 'qpp2026', name: 'QPP 2026 Readiness Support', amount: 'Advisory', focus: 'ESG compliance', url: 'https://www.qld.gov.au/procurement' },
  ],
  NSW: [
    { id: 'nzmi', name: 'Net Zero Manufacturing Initiative', amount: 'Up to $500,000', focus: 'Clean manufacturing', url: 'https://www.investment.nsw.gov.au/' },
    { id: 'spp', name: 'Sustainable Procurement Policy Support', amount: 'Various', focus: 'Government suppliers', url: 'https://buy.nsw.gov.au/sustainability' },
    { id: 'eff', name: 'Energy Efficiency Fund', amount: 'Up to $50,000', focus: 'Energy upgrades', url: 'https://www.energy.nsw.gov.au/' },
  ],
  VIC: [
    { id: 'spf', name: 'Sustainable Procurement Framework', amount: 'Various', focus: 'State government contracts', url: 'https://www.buyingfor.vic.gov.au/sustainability' },
    { id: 'bee', name: 'Business Energy Efficiency', amount: 'Up to $20,000', focus: 'SME energy', url: 'https://www.energy.vic.gov.au/' },
    { id: 'smeg', name: 'SME Green Manufacturing', amount: 'Up to $100,000', focus: 'Clean tech', url: 'https://business.vic.gov.au/' },
  ],
  SA: [
    { id: 'saef', name: 'SA Energy Efficiency Scheme', amount: 'Rebates', focus: 'Energy reduction', url: 'https://www.sa.gov.au/topics/energy-and-environment' },
    { id: 'sagp', name: 'Green Procurement SA', amount: 'Various', focus: 'Sustainable suppliers', url: 'https://www.procurement.sa.gov.au/' },
  ],
  WA: [
    { id: 'waef', name: 'WA Energy Transformation', amount: 'Various', focus: 'Renewables', url: 'https://www.wa.gov.au/organisation/energy-policy-wa' },
    { id: 'smer', name: 'SME Recovery Fund', amount: 'Up to $25,000', focus: 'Business resilience', url: 'https://www.smallbusiness.wa.gov.au/' },
  ],
  TAS: [
    { id: 'tasgp', name: 'Tasmanian Green Growth', amount: 'Up to $50,000', focus: 'Sustainable business', url: 'https://www.business.tas.gov.au/' },
    { id: 'reap', name: 'Renewable Energy Action Plan', amount: 'Various', focus: 'Clean energy', url: 'https://www.stategrowth.tas.gov.au/' },
  ],
  NT: [
    { id: 'ntbg', name: 'NT Business Growth', amount: 'Up to $20,000', focus: 'SME expansion', url: 'https://nt.gov.au/industry' },
    { id: 'ntsus', name: 'NT Sustainability Initiative', amount: 'Various', focus: 'Regional ESG', url: 'https://nt.gov.au/environment' },
  ],
  ACT: [
    { id: 'actcl', name: 'ACT Climate Choice', amount: 'Rebates', focus: 'Carbon neutral', url: 'https://www.climatechoices.act.gov.au/' },
    { id: 'actsb', name: 'Sustainable Business ACT', amount: 'Various', focus: 'Green business', url: 'https://www.business.act.gov.au/' },
  ]
};

const australianStates = [
  { code: 'QLD', name: 'Queensland' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'Australian Capital Territory' }
];

const GrantToolPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    state: '',
    primary_industry: '',
    business_size: '',
    annual_turnover: '',
    project_goal: ''
  });
  const [showResults, setShowResults] = useState(false);

  const totalSteps = 5;

  const questions = [
    {
      id: 'state',
      title: 'Where is your business located?',
      subtitle: 'Select your State or Territory',
      icon: MapPin,
      type: 'select'
    },
    {
      id: 'primary_industry',
      title: 'What is your primary industry?',
      subtitle: 'This helps match industry-specific grants',
      icon: Building2,
      type: 'radio',
      options: [
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'construction', label: 'Construction & Infrastructure' },
        { value: 'professional', label: 'Professional Services' },
        { value: 'agriculture', label: 'Agriculture / Primary Production' },
        { value: 'tourism', label: 'Tourism / Hospitality' },
        { value: 'retail', label: 'Retail & Wholesale' },
        { value: 'other', label: 'Other' }
      ]
    },
    {
      id: 'business_size',
      title: 'How many employees do you have?',
      subtitle: 'Including full-time and part-time staff',
      icon: Building2,
      type: 'radio',
      options: [
        { value: 'sole', label: 'Sole Trader' },
        { value: 'micro', label: '2–19 employees' },
        { value: 'small', label: '20–99 employees' },
        { value: 'medium', label: '100–199 employees' },
        { value: 'large', label: '200+ employees' }
      ]
    },
    {
      id: 'annual_turnover',
      title: 'What is your annual turnover?',
      subtitle: 'Last financial year',
      icon: DollarSign,
      type: 'radio',
      options: [
        { value: 'under500k', label: 'Under $500,000' },
        { value: '500k-2m', label: '$500,000 – $2 Million' },
        { value: '2m-10m', label: '$2 Million – $10 Million' },
        { value: 'over10m', label: 'Over $10 Million' }
      ]
    },
    {
      id: 'project_goal',
      title: 'What is your primary ESG goal?',
      subtitle: 'Select the best match',
      icon: Target,
      type: 'radio',
      options: [
        { value: 'energy', label: 'Reduce energy costs (LEDs, efficient equipment)' },
        { value: 'carbon', label: 'Track & report carbon emissions' },
        { value: 'procurement', label: 'Win government or corporate tenders' },
        { value: 'equipment', label: 'Upgrade to cleaner manufacturing' },
        { value: 'compliance', label: 'Meet new ESG compliance requirements' }
      ]
    }
  ];

  const currentQuestion = questions[currentStep - 1];

  // Calculate matched grants
  const getMatchedGrants = () => {
    if (!formData.state) return [];
    const stateGrants = GRANT_DATABASE[formData.state] || [];
    
    // Simple matching logic based on responses
    return stateGrants.filter(grant => {
      if (formData.primary_industry === 'manufacturing' && grant.focus.toLowerCase().includes('manufactur')) return true;
      if (formData.project_goal === 'energy' && grant.focus.toLowerCase().includes('energy')) return true;
      if (formData.project_goal === 'procurement' && (grant.focus.toLowerCase().includes('procurement') || grant.focus.toLowerCase().includes('supplier'))) return true;
      if (formData.project_goal === 'compliance' && grant.focus.toLowerCase().includes('compliance')) return true;
      return true; // Return all state grants as matches for now
    });
  };

  const matchedGrants = getMatchedGrants();

  const handleNext = () => {
    const value = formData[currentQuestion.id];
    if (!value) {
      toast.error('Please select an option to continue');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Results View (Lead Magnet)
  if (showResults) {
    const stateName = australianStates.find(s => s.code === formData.state)?.name || formData.state;
    const industryLabel = questions[1].options.find(o => o.value === formData.primary_industry)?.label || formData.primary_industry;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] py-12 px-6" data-testid="grant-results">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-[#74A57F] to-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Great News!
            </h1>
          </motion.div>

          {/* Summary Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 mb-8"
          >
            <div className="text-center">
              <p className="text-lg text-slate-600 mb-4">Results Found</p>
              <div className="text-6xl font-bold text-[#74A57F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="grant-count">
                {matchedGrants.length}
              </div>
              <p className="text-xl text-[#003B5C] font-medium">
                Grants for <strong>{industryLabel}</strong> in <strong>{stateName}</strong>
              </p>
            </div>
            
            {/* Preview of grants (names only, blurred) */}
            <div className="mt-8 space-y-3">
              {matchedGrants.slice(0, 3).map((grant, idx) => (
                <div 
                  key={grant.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#003B5C]/10 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-[#003B5C]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#003B5C]">{grant.name}</p>
                      <p className="text-sm text-slate-500">{grant.amount}</p>
                    </div>
                  </div>
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
              ))}
              
              {matchedGrants.length > 3 && (
                <div className="text-center py-4 text-slate-500 bg-slate-50/50 rounded-xl border border-dashed border-slate-300">
                  <Lock className="w-5 h-5 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm">+ {matchedGrants.length - 3} more grants available</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Lead Magnet CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#003B5C] to-[#005580] rounded-2xl p-8 text-center text-white"
          >
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Unlock Your Full Grant Matches
            </h2>
            <p className="text-white/80 mb-6">
              Sign up for a <strong>free account</strong> to view detailed grant information, eligibility criteria, and application links.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button 
                  className="bg-white text-[#003B5C] hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-medium w-full sm:w-auto"
                  data-testid="view-matches-btn"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  View My Matches (Free)
                </Button>
              </Link>
            </div>
            
            <p className="text-white/60 text-sm mt-4">
              Free trial includes 5 emissions entries • No credit card required
            </p>
          </motion.div>

          {/* Back Button */}
          <div className="mt-8 text-center">
            <Button
              onClick={handleBack}
              variant="outline"
              className="border-[#003B5C] text-[#003B5C]"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Modify Answers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] py-12 px-6" data-testid="grant-tool-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Grant Eligibility Tool
          </h1>
          <p className="text-lg text-slate-600">
            Answer 5 quick questions to discover Australian grants for your business
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-[#003B5C]">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm text-slate-600">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-white/50 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#003B5C] to-[#74A57F]"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl"
          >
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-[#003B5C]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <currentQuestion.icon className="w-6 h-6 text-[#003B5C]" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="question-title">
                  {currentQuestion.title}
                </h2>
                <p className="text-slate-500 mt-1">{currentQuestion.subtitle}</p>
              </div>
            </div>

            {currentQuestion.type === 'select' ? (
              <Select
                value={formData[currentQuestion.id]}
                onValueChange={(value) => setFormData({ ...formData, [currentQuestion.id]: value })}
              >
                <SelectTrigger className="h-14 text-lg" data-testid="state-select">
                  <SelectValue placeholder="Select your state or territory" />
                </SelectTrigger>
                <SelectContent>
                  {australianStates.map((state) => (
                    <SelectItem key={state.code} value={state.code} className="text-lg py-3">
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <RadioGroup
                value={formData[currentQuestion.id]}
                onValueChange={(value) => setFormData({ ...formData, [currentQuestion.id]: value })}
                className="space-y-3"
              >
                {currentQuestion.options.map((option) => (
                  <div 
                    key={option.value} 
                    className="flex items-center space-x-3 p-4 rounded-xl hover:bg-[#F8F9FA] transition-colors cursor-pointer border border-slate-200 hover:border-[#74A57F]"
                  >
                    <RadioGroupItem value={option.value} id={option.value} data-testid={`option-${option.value}`} />
                    <Label htmlFor={option.value} className="flex-1 cursor-pointer text-slate-700 text-base">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className="rounded-full px-6 py-6 border-[#003B5C] text-[#003B5C] disabled:opacity-50"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            className="bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full px-6 py-6"
            data-testid="next-button"
          >
            {currentStep === totalSteps ? 'View Results' : 'Next'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Member Link */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/dashboard" className="text-[#003B5C] font-medium hover:underline">
              Access Grant Readiness Vault →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GrantToolPage;
